import { getInteractiveChildren } from "./utilities.js";

/**
 * @example
 * Usage in HTML:
 * ```html
 * <div
 *  class="js-dropdown"
 *  data-dropdown-id="target-id"
 * >
 *  <a data-dropdown-fallback="target-id" href="…">
 *    Fallback link
 *  <a>
 *
 *  <button data-dropdown-toggle="target-id" type="button">
 *    Toggle button
 *  </button>
 *
 *  <div id="target-id" hidden>
 *    <button data-dropdown-close="target-id" type="button">
 *      Close
 *    </button>
 *
 *    Target content
 *
 *    <div data-dropdown-overlay="target-id"></div>
 *  </div>
 * </div>
 * ```
 * @example
 * Optional attributes on root element:
 * ```
 * data-dropdown-close-on-outside
 * data-dropdown-close-on-escape
 * data-dropdown-disable-scroll
 * data-dropdown-trap-focus
 * data-dropdown-reset-focus="element-id"
 * ```
 */
export default class Dropdown {
  #isOpen = false;

  #isTabKey = false;

  /**
   * Create new dropdown
   *
   * @param {HTMLElement} container
   */
  constructor(container) {
    this.options = {
      id: container.getAttribute("data-dropdown-id") ?? "",
      closeOnOutside: container.hasAttribute("data-dropdown-close-on-outside"),
      closeOnEscape: container.hasAttribute("data-dropdown-close-on-escape"),
      disableScroll: container.hasAttribute("data-dropdown-disable-scroll"),
      trapFocus: container.hasAttribute("data-dropdown-trap-focus"),
      resetFocus: container.getAttribute("data-dropdown-reset-focus"),
    };

    this.elements = {
      container,

      fallback: /** @type {HTMLAnchorElement|null} */ (
        container.querySelector(`[data-dropdown-fallback="${this.options.id}"]`)
      ),
      toggle: /** @type {HTMLButtonElement|null} */ (
        container.querySelector(`[data-dropdown-toggle="${this.options.id}"]`)
      ),
      target: /** @type {HTMLElement|null} */ (
        document.getElementById(this.options.id)
      ),
      overlay: /** @type {HTMLElement|null} */ (
        container.querySelector(`[data-dropdown-overlay="${this.options.id}"]`)
      ),
      close: /** @type {HTMLButtonElement|null} */ (
        container.querySelector(`[data-dropdown-close="${this.options.id}"]`)
      ),
      resetFocus: /** @type {HTMLElement|null} */ (
        document.getElementById(this.options.resetFocus ?? "")
      ),
    };

    this.elements.toggle?.setAttribute("aria-controls", this.options.id);
    this.elements.toggle?.setAttribute("aria-expanded", "false");
    this.elements.close?.setAttribute("aria-controls", this.options.id);

    this.elements.fallback?.setAttribute("hidden", "");
    this.elements.toggle?.removeAttribute("hidden");
    this.elements.target?.setAttribute("aria-hidden", "true");

    if (
      this.elements.resetFocus &&
      !["A", "BUTTON"].includes(this.elements.resetFocus.tagName)
    ) {
      this.elements.resetFocus.setAttribute("tabindex", "-1");
    }

    this.handleCloseOnOutside = this.handleCloseOnOutside.bind(this);
    this.handleCloseOnEscape = this.handleCloseOnEscape.bind(this);

    this.elements.toggle?.addEventListener("click", this.toggle.bind(this));
    this.elements.close?.addEventListener("click", this.close.bind(this));

    if (this.options.trapFocus) {
      this.elements.target?.addEventListener(
        "keydown",
        this.handleTabKey.bind(this),
      );
      this.elements.target?.addEventListener(
        "focusout",
        this.handleTargetFocusOut.bind(this),
      );
      this.elements.toggle?.addEventListener(
        "focusout",
        this.handleToggleFocusOut.bind(this),
      );
    }

    container
      .querySelectorAll(".header-dropdown-list__item")
      .forEach((item) => {
        if (item.textContent.trim() === "ホーム") {
          item.classList.add("header-dropdown-list__item--desktop-only");
        }
      });
  }

  /**
   * Handle tab key in menu
   *
   * @param {KeyboardEvent} event
   */
  handleTabKey(event) {
    if (event.key === "Tab") {
      this.#isTabKey = true;
    } else {
      this.#isTabKey = false;
    }
  }

  /**
   * Toggle dropdown target
   *
   * @param {PointerEvent} event
   */
  toggle(event) {
    const isKeyboard = event.detail === 0;

    if (this.#isOpen) {
      this.close({ isKeyboard });
    } else {
      this.open({ isKeyboard });
    }
    this.#isTabKey = false;
  }

  /**
   * Open dropdown target
   *
   * @param {object} [options]
   * @param {boolean} [options.isKeyboard]
   */
  open({ isKeyboard = false } = {}) {
    this.elements.target?.removeAttribute("hidden");
    this.elements.target?.setAttribute("aria-hidden", "false");
    this.elements.toggle?.setAttribute("aria-expanded", "true");

    if (this.options.closeOnOutside) {
      document.body.addEventListener("click", this.handleCloseOnOutside);
    }

    if (this.options.closeOnEscape) {
      document.body.addEventListener("keyup", this.handleCloseOnEscape);
    }

    if (this.options.disableScroll) {
      document.documentElement.style.setProperty("overflow", "hidden");
    }

    if (this.options.trapFocus && isKeyboard && this.elements.target) {
      const children = getInteractiveChildren(this.elements.target);
      children.at(0)?.focus();
    }

    this.#isOpen = true;
  }

  /**
   * Close dropdown target
   *
   * @param {object} [options]
   * @param {boolean} [options.isKeyboard]
   */
  close({ isKeyboard = false } = {}) {
    this.elements.target?.setAttribute("hidden", "");
    this.elements.target?.setAttribute("aria-hidden", "true");
    this.elements.toggle?.setAttribute("aria-expanded", "false");

    if (this.options.closeOnOutside) {
      document.body.removeEventListener("click", this.handleCloseOnOutside);
    }

    if (this.options.closeOnEscape) {
      document.body.removeEventListener("keyup", this.handleCloseOnEscape);
    }

    if (this.options.disableScroll) {
      document.documentElement.style.removeProperty("overflow");
    }

    if (this.options.resetFocus && isKeyboard) {
      this.elements.resetFocus?.focus();
    }

    this.#isOpen = false;
  }

  /**
   * Handle click outside dropdown
   *
   * @param {MouseEvent} event
   */
  handleCloseOnOutside(event) {
    const clickedElement = /** @type {HTMLElement|null} */ (event.target);

    const isClickOutside =
      !this.elements.container.contains(clickedElement) ||
      clickedElement === this.elements.overlay;

    if (isClickOutside) {
      this.close();
    }
  }

  /**
   * Handle Escape key press
   *
   * @param {KeyboardEvent} event
   */
  handleCloseOnEscape(event) {
    if (event.key === "Escape") {
      this.close({ isKeyboard: true });
    }
  }

  /**
   * Is handle focus out event
   *
   * @param {FocusEvent} event
   * @returns {boolean} isHandleFocusOut
   */
  isHandleFocusOut(event) {
    const isKeyboard =
      event.relatedTarget !== document.querySelector("header#header");

    const isMobile =
      window.matchMedia && window.matchMedia("(max-width: 68rem)").matches;

    const isMobileAndClick = isMobile && !this.#isTabKey;

    if (
      !this.#isOpen ||
      !this.elements.toggle ||
      !this.elements.target ||
      !isKeyboard ||
      isMobileAndClick
    ) {
      return false;
    }
    return true;
  }

  /**
   * Handle focusout event on target
   *
   * @param {FocusEvent} event
   */
  handleTargetFocusOut(event) {
    if (!this.isHandleFocusOut(event)) {
      return;
    }

    const children = getInteractiveChildren(this.elements.target);

    if (
      event.target === children.at(-1) &&
      event.relatedTarget !== children.at(-2)
    ) {
      this.elements.toggle.focus();
    }
  }

  /**
   * Handle focusout event on toggle
   *
   * @param {FocusEvent} event
   */
  handleToggleFocusOut(event) {
    if (!this.isHandleFocusOut(event)) {
      return;
    }

    const children = getInteractiveChildren(this.elements.target);

    if (event.relatedTarget !== children.at(0)) {
      children.at(-1)?.focus();
    }
  }
}
